import { useCallback, useEffect, useReducer } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import BaseButton from '@/components/ui/BaseButton';
import BasePaper from '@/components/ui/BasePaper';
import OverviewInfoCard from '@/components/overview/OverviewInfoCard';
import '@/styles/pages/StoreMgmt/overview/StoreOverviewPage.scss';
import { GridView, ConnectedTv, PendingActions, CellTower } from '@mui/icons-material';
import BaseTitle from '@/components/ui/BaseTitle';
import NoStoreSelectionBlock from '@/components/store/NoStoreSelectionBlock';
import { fetchStoreOverviewAPI } from '@/api/storeManagement/overview';
import { useAppContext } from '@/provider/contexts';
import { StoreOverviewResponse, StoreOverviewPageAction } from '@/types/pages/storeManagement/overview';
import { useTranslation } from 'react-i18next';
import { DashboardCards, OVERVIEW_TYPE } from '@/types/pages/dashboard';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';

function reducer(state: DashboardCards, action: StoreOverviewPageAction) {
    switch (action.type) {
        case 'SET_CARDS_LOADING':
            return {
                ...state,
                [OVERVIEW_TYPE.AP]: {
                    ...state[OVERVIEW_TYPE.AP],
                    loading: action.payload,
                },
                [OVERVIEW_TYPE.TAG]: {
                    ...state[OVERVIEW_TYPE.TAG],
                    loading: action.payload,
                },
                [OVERVIEW_TYPE.JOB]: {
                    ...state[OVERVIEW_TYPE.JOB],
                    loading: action.payload,
                },
            };
        case 'SET_STORE_OVERVIEW_CARDS': {
            const {
                isLoading,
                hasData,
                onlineTagsRatioNum,
                jobTagsRatioNum,
                apStatusNum,
                tagsOnlineNum,
                tagsTotalNum,
                jobTagsSuccessNum,
                jobTagsTotalNum,
                apStatusOnlineNum,
                apStatusTotalNum,
            } = action.payload;
            return {
                ...state,
                [OVERVIEW_TYPE.AP]: {
                    loading: isLoading,
                    hasData,
                    online: apStatusOnlineNum,
                    max: apStatusTotalNum,
                    ratioNum: apStatusNum,
                },
                [OVERVIEW_TYPE.TAG]: {
                    loading: isLoading,
                    hasData,
                    online: tagsOnlineNum,
                    max: tagsTotalNum,
                    ratioNum: onlineTagsRatioNum,
                },
                [OVERVIEW_TYPE.JOB]: {
                    loading: isLoading,
                    hasData,
                    online: jobTagsSuccessNum,
                    max: jobTagsTotalNum,
                    ratioNum: jobTagsRatioNum,
                },
            };
        }
        default:
            return state;
    }
}

const StoreOverviewPage = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const defaultState: DashboardCards = {
        [OVERVIEW_TYPE.AP]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
        [OVERVIEW_TYPE.TAG]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
        [OVERVIEW_TYPE.JOB]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
    };

    const [state, dispatch] = useReducer(reducer, defaultState);

    const { manageStoreId, userDetails, corporationDetails, setNotification } = useAppContext();
    const { isStoreReady } = useOutletContext<StoreMgmtOutletContext>();

    const fetchStoreOverview = useCallback(async () => {
        if (!isStoreReady) return;
        dispatch({ type: 'SET_CARDS_LOADING', payload: true });
        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchStoreOverviewAPI(manageStoreId as number);
            if (isSuccess) {
                const cardsData = getOverviewCardsData(data);

                dispatch({
                    type: 'SET_STORE_OVERVIEW_CARDS',
                    payload: { isLoading: false, hasData: true, ...cardsData },
                });
                return;
            }
            throw new Error(message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Fetch Overview'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', error);
        }
    }, [manageStoreId, isStoreReady]);

    function getOverviewCardsData(data: StoreOverviewResponse) {
        const overviewCards = {
            onlineTagsRatioNum: ['0', '00'],
            jobTagsRatioNum: ['0', '00'],
            apStatusNum: ['0', '00'],
            tagsOnlineNum: 0,
            tagsTotalNum: 0,
            jobTagsSuccessNum: 0,
            jobTagsTotalNum: 0,
            apStatusOnlineNum: 0,
            apStatusTotalNum: 0,
        };
        if (data.online_tags_ratio.max_tags) {
            const ratio = ((data.online_tags_ratio.online_tags / data.online_tags_ratio.max_tags) * 100).toFixed(2);
            overviewCards.onlineTagsRatioNum = ratio.split('.');
            overviewCards.tagsOnlineNum = data.online_tags_ratio.online_tags;
            overviewCards.tagsTotalNum = data.online_tags_ratio.max_tags;
        }

        if (data.job_tags_ratio.max_jobs) {
            const ratio = ((data.job_tags_ratio.success_jobs / data.job_tags_ratio.max_jobs) * 100).toFixed(2);
            overviewCards.jobTagsRatioNum = ratio.split('.');
            overviewCards.jobTagsSuccessNum = data.job_tags_ratio.success_jobs;
            overviewCards.jobTagsTotalNum = data.job_tags_ratio.max_jobs;
        }

        if (data.ap_status.max_aps) {
            const ratio = ((data.ap_status.online / data.ap_status.max_aps) * 100).toFixed(2);
            overviewCards.apStatusNum = ratio.split('.');
            overviewCards.apStatusOnlineNum = data.ap_status.online;
            overviewCards.apStatusTotalNum = data.ap_status.max_aps;
        }

        return overviewCards;
    }

    const goToMobilePage = () => {
        if (manageStoreId) {
            navigate(`/mobile/${manageStoreId}`);
        } else {
            console.warn('Store ID is missing. Cannot navigate to Mobile Page.');
        }
    };

    const cardClickHandler = (type: string) => {
        if (!userDetails) return;
        const hasStoreDevicePermission = ['*', 'page-storeDevice-all-read'].some((e) =>
            corporationDetails.user_abilities.includes(e),
        );
        if (userDetails.user_level >= 2 || hasStoreDevicePermission)
            navigate('/store_management/device', { state: { from: type } });
    };

    useEffect(() => {
        fetchStoreOverview();
    }, [fetchStoreOverview]);

    return (
        <div className="store-overview-page">
            <BasePaper className="overview-section">
                <BaseTitle icon={<GridView />} title={t('Overview')} />
                {manageStoreId && (
                    <BaseButton style={{ marginBottom: '16px' }} onClick={goToMobilePage} variant="contained">
                        {t('Go to Mobile Page')}
                    </BaseButton>
                )}
                {manageStoreId ? (
                    <>
                        <div className="overview-cards-wrapper">
                            <OverviewInfoCard
                                info={state[OVERVIEW_TYPE.TAG]}
                                title={t('Device Ratio')}
                                icon={<ConnectedTv />}
                                isClickable
                                onClick={() => cardClickHandler('device-ratio')}
                            />
                            <OverviewInfoCard
                                info={state[OVERVIEW_TYPE.JOB]}
                                title={t('Job Status Ratio')}
                                icon={<PendingActions />}
                                isClickable
                                onClick={() => cardClickHandler('job-ratio')}
                            />
                            <OverviewInfoCard
                                info={state[OVERVIEW_TYPE.AP]}
                                title={t('AP Ratio')}
                                icon={<CellTower />}
                                isClickable
                                onClick={() => cardClickHandler('ap-ratio')}
                            />
                        </div>
                    </>
                ) : (
                    <NoStoreSelectionBlock />
                )}
            </BasePaper>
        </div>
    );
};

export default StoreOverviewPage;
